import React, { useCallback, useEffect, useRef, useState } from 'react'
import './PlayerRecommendation.scss'
import Image from '@components/Image/Image'
import { useImgUrlTransform } from '@hooks/useImgUrlTransform'
import { ImageExtension, ImageSize } from '@enums/images.enum'
import ActionButton from '@components/Buttons/ActionButton/ActionButton'
import { ButtonIcon } from '@constants/index'
import Text from '@components/Text/Text'
import { TextTag } from '@enums/textTag'
import CTAButton from '@components/Buttons/CTAButton/CTAButton'
import { VelvetSDK } from 'sdk'
import { BackArrow } from '@assets/index'
import PrimaryPlayerButton from '@components/PlayerControls/PrimaryPlayerButton/PrimaryPlayerButton'
import { useNavigate } from 'react-router-dom'
import { categoryType } from '@constants/category.const'
import classNames from 'classnames'
import { routes } from '@constants/routes.const'
import { getActiveProfile, onBackKeyPress } from '@utils/helpers'
import FallbackImage from '@components/FallbackImage/FallbackImage'
import { btnRecommendationTimeout } from '@constants/player.const'
import { MediaItem } from 'sdk/services/videoPlayer/types/videoPlayer'
import { VideoEntitlementType } from 'sdk/services/cms/types/cmsService'

interface PlayerRecommendationProps {
  videoInfo: VideoEntitlementType | null
  recommendedData: MediaItem | null
  isRecommendedFetched: boolean
  userCountry: string
}

const PlayerRecommendation = ({
  videoInfo,
  recommendedData,
  isRecommendedFetched,
  userCountry,
}: PlayerRecommendationProps) => {
  const V = VelvetSDK.Vocabulary.vocabulary
  const navigate = useNavigate()
  const [parentItemData, setParentItemData] = useState<MediaItem | null>()
  const trailerCountdownSec: number = VelvetSDK.Configuration.configuration.features.recommendations.trailerCountdownSec
  const [countdown, setCountdown] = useState(trailerCountdownSec)
  const [trailerCountdown, setTrailerCountdown] = useState(trailerCountdownSec)
  const [isParentDataFetched, setIsParentDataFetched] = useState(false)
  const ctaPlayButtonRef = useRef<HTMLButtonElement>(null)
  const ctaButtonTrailerRef = useRef<HTMLButtonElement>(null)
  const ctaNextEpisodeButtonRef = useRef<HTMLButtonElement>(null)
  const movie = videoInfo?.parentType === categoryType.MOVIE
  const serie = videoInfo?.parentType === categoryType.SERIE
  const navigateBackFromPlayer = sessionStorage.getItem('navigateBackFromPlayer')
  const activeProfile = getActiveProfile()

  const showCurrentEpisodeInfoCondition =
    videoInfo?.parentType === categoryType.SERIE &&
    recommendedData?.seasonIndex !== undefined &&
    recommendedData?.episodeIndex !== undefined

  const showPrevEpisodeInfoCondition =
    videoInfo?.parentType === categoryType.SERIE &&
    parentItemData &&
    videoInfo?.seasonIndex !== undefined &&
    videoInfo?.episodeIndex !== undefined

  const showPrevEpisodeInfo =
    videoInfo?.seasonIndex !== undefined &&
    videoInfo?.episodeIndex !== undefined &&
    `S${videoInfo.seasonIndex + 1}: E${videoInfo?.episodeIndex + 1} ${videoInfo?.title}`

  const showCurrentEpisodeInfo =
    recommendedData?.seasonIndex !== undefined &&
    recommendedData?.episodeIndex !== undefined &&
    `S${recommendedData?.seasonIndex + 1}: E${recommendedData?.episodeIndex + 1} ${recommendedData?.data?.title}`

  const getBackdropImage = () => {
    if (movie) {
      return recommendedData?.contextData?.imageBackdrop || recommendedData?.contextData?.Thumbnail
    } else {
      // show the backdrop image from the series
      if (videoInfo?.parentSlug === recommendedData?.parentSlug) {
        return parentItemData?.contextData?.imageBackdrop
      } else if (videoInfo?.parentSlug !== recommendedData?.slug) {
        // If there are no more episodes from the serie, show the backdrop from the recommended series
        return recommendedData?.contextData?.imageBackdrop
      }
    }
  }

  const getLogoImage = () => {
    if (movie) {
      return recommendedData?.contextData?.imageTitle
    } else {
      // show the logo image from the series
      if (videoInfo?.parentSlug === recommendedData?.parentSlug) {
        return parentItemData?.contextData?.imageTitle ?? ''
      } else if (videoInfo?.parentSlug !== recommendedData?.slug) {
        // If there are no more episodes from the serie, show the title from the recommended series
        return recommendedData?.contextData?.imageTitle ?? ''
      }
    }
  }

  const transformedImage = useImgUrlTransform(getBackdropImage() ?? '', ImageSize.VIDEO_THUMBNAIL)
  const transformedImageLogo = useImgUrlTransform(getLogoImage() ?? '', ImageSize.TITLE, ImageExtension.PNG)

  const handleNavigateToDetailsPage = useCallback(() => {
    recommendedData && navigate(recommendedData?.callToActions?.webUrl)
  }, [navigate, recommendedData])

  const handleNavigateToAllEpisodes = useCallback(() => {
    navigate(`/${recommendedData?.parentType}/${recommendedData?.parentSlug}`)
  }, [navigate, recommendedData])

  const handleNavigateToPlayer = useCallback(() => {
    if (movie) {
      navigate(`${routes.PLAYER}/${recommendedData?.slug}`)
    } else {
      recommendedData?.episodeSlug
        ? navigate(`${routes.PLAYER}/${recommendedData?.episodeSlug}`)
        : navigate(`${routes.PLAYER}/${recommendedData?.playable?.chapter.slug}`)
    }

    navigate(0)
  }, [movie, navigate, recommendedData])

  const handleGoBack = useCallback(() => {
    navigateBackFromPlayer ? navigate(navigateBackFromPlayer) : navigate(-1)
  }, [navigate, navigateBackFromPlayer])

  const handleNavigateToTrailer = useCallback(() => {
    // Will be covered in another ticket
    console.log('Navigate to trailer')
  }, [])

  const closePlayerOnBackButton = (e: KeyboardEvent) =>
    onBackKeyPress(e, () => navigateBackFromPlayer && navigate(navigateBackFromPlayer))

  useEffect(() => {
    if (serie && !isParentDataFetched) {
      setIsParentDataFetched(true)

      VelvetSDK.CMS.service()
        .fetchDetails(videoInfo?.parentType, videoInfo?.parentSlug, true, activeProfile?.preferredLanguage, userCountry)
        .then((data: MediaItem) => {
          setParentItemData(data)
        })
    }
  }, [
    activeProfile?.preferredLanguage,
    isParentDataFetched,
    serie,
    userCountry,
    videoInfo?.parentSlug,
    videoInfo?.parentType,
  ])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    if (movie || (serie && !recommendedData?.data)) {
      if (trailerCountdown > 0) {
        timer = setTimeout(() => {
          setTrailerCountdown((prev) => prev - 1)
        }, btnRecommendationTimeout)
      } else if (trailerCountdown === 0) {
        handleNavigateToTrailer()
      }
    } else if (serie && videoInfo?.parentSlug === recommendedData?.parentSlug) {
      if (countdown > 0) {
        timer = setTimeout(() => {
          setCountdown((prev) => prev - 1)
        }, btnRecommendationTimeout)
      } else if (countdown === 0) {
        handleNavigateToPlayer()
      }
    }
    return () => clearTimeout(timer)
  }, [
    countdown,
    handleNavigateToPlayer,
    handleNavigateToTrailer,
    movie,
    recommendedData?.data,
    recommendedData?.parentSlug,
    serie,
    trailerCountdown,
    videoInfo?.parentSlug,
  ])

  useEffect(() => {
    ctaPlayButtonRef && ctaPlayButtonRef.current?.focus()
    ctaNextEpisodeButtonRef && ctaNextEpisodeButtonRef.current?.focus()
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', closePlayerOnBackButton, true)

    return () => document.removeEventListener('keydown', closePlayerOnBackButton, true)
  })

  return (
    <>
      {videoInfo && recommendedData && isRecommendedFetched && recommendedData && !recommendedData.error && (
        <div className="player-recommendation">
          {recommendedData?.contextData?.imageBackdrop ? (
            <Image width="100vw" height="100vh" image={transformedImage} fallbackOnLoad />
          ) : (
            <FallbackImage className="backdrop-image" />
          )}
          <div className="back-buttons">
            <PrimaryPlayerButton onClick={handleGoBack} className="back-button">
              <BackArrow />
            </PrimaryPlayerButton>
            <div className={`title ${videoInfo?.parentType === categoryType.SERIE && 'serie'}`}>
              {showPrevEpisodeInfoCondition ? (
                <div className="episode-info">
                  <Text tag={TextTag.P}>{parentItemData.title}</Text>
                  <Text tag={TextTag.P}>{showPrevEpisodeInfo}</Text>
                </div>
              ) : (
                videoInfo.title
              )}
            </div>
          </div>
          <div className="additional-info">
            <Text tag={TextTag.P} className="you-may-also-like">
              {V.recommendationYouMayLike}
            </Text>
            {recommendedData?.contextData?.imageTitle ? (
              <Image image={transformedImageLogo} fallbackOnLoad className="logo-img-player" />
            ) : (
              <FallbackImage className="logo-image" />
            )}
            {showCurrentEpisodeInfoCondition && <div className="episode-info">{showCurrentEpisodeInfo}</div>}
            <Text tag={TextTag.P} className="description">
              {recommendedData?.description || recommendedData?.data?.description}
            </Text>
            <div className="buttons">
              {(movie || (serie && !recommendedData.data)) && (
                <>
                  <CTAButton
                    icon={ButtonIcon.PLAY}
                    contentCategory={''}
                    buttonText={V.recommendationButtonLabelPlay}
                    onClick={handleNavigateToPlayer}
                    ctaButtonRef={ctaPlayButtonRef}
                    className="scroll-center"
                    id="play-recommendation"
                  />
                  <CTAButton
                    icon={ButtonIcon.PLAY}
                    contentCategory={''}
                    buttonText={`${V.recommendationButtonLabelTrailer}${
                      trailerCountdown > 0 ? ` ${V.recommendationButtonLabelIn} ${trailerCountdown}` : ''
                    }`}
                    onClick={handleNavigateToTrailer}
                    ctaButtonRef={ctaButtonTrailerRef}
                    className={classNames('scroll-center', trailerCountdown > 0 && 'trailer-countdown')}
                    id="trailer-btn"
                  />
                  <ActionButton
                    icon={ButtonIcon.INFO}
                    onClick={handleNavigateToDetailsPage}
                    id="info-btn-player"
                    className="scroll-center"
                  />
                </>
              )}
              {serie && videoInfo?.parentSlug === recommendedData.parentSlug && (
                <>
                  <CTAButton
                    icon={ButtonIcon.PLAY}
                    contentCategory={''}
                    buttonText={`${V.recommendationButtonNextEpisode}${
                      countdown > 0 ? ` ${V.recommendationButtonLabelIn} ${countdown}` : ''
                    }`}
                    onClick={handleNavigateToPlayer}
                    ctaButtonRef={ctaNextEpisodeButtonRef}
                    className="scroll-center"
                    id="next-episode-btn"
                  />
                  <CTAButton
                    contentCategory={''}
                    buttonText={V.recommendationButtonSeeAllEpisodes}
                    onClick={handleNavigateToAllEpisodes}
                    ctaButtonRef={ctaButtonTrailerRef}
                    className="scroll-center"
                    id="trailer"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PlayerRecommendation
